import { useEffect, useState } from "react";
import Select from "react-select";
import { useFetchData } from "../../hooks/useFetch";
import { ApiConfig, API_ORDER_ROUTER } from "../../services/api-config";

// Nhân sự được chỉ định đi NHẬN hàng = tài xế (Giao hàng) ∪ NV kiểm đếm (Kiểm đếm),
// theo chi nhánh, kèm số đơn đang treo. Lấy từ ORDER service /assignable-drivers.
export interface PickupStaff {
  id: number;
  name?: string;
  phone?: string;
  is_driver?: number;
  is_counter?: number;
  role_label?: string;
  pending_count?: number;
}

interface Props {
  branchId: number | null;
  value: PickupStaff[];
  onChange: (selected: PickupStaff[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Lọc pool: 'driver' = chỉ tài xế, 'counter' = chỉ NV kiểm đếm. Bỏ trống = cả hai (như cũ). */
  filterRole?: 'driver' | 'counter';
}

const PickupStaffSelect = ({ branchId, value, onChange, placeholder, disabled, filterRole }: Props) => {
  const { request: fetchStaff } = useFetchData.Get<PickupStaff[]>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const [staff, setStaff] = useState<PickupStaff[]>([]);

  useEffect(() => {
    if (!branchId) {
      setStaff([]);
      return;
    }
    let cancelled = false;
    fetchStaff({
      endPoint: API_ORDER_ROUTER.API_ASSIGNABLE_DRIVERS,
      data: { branch_id: branchId },
    }).then((res: any) => {
      if (cancelled) return;
      const list = res?.data ?? [];
      setStaff(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // Option: "Họ tên - Vai trò - N đơn treo"
  const buildLabel = (s: PickupStaff) => {
    const parts: string[] = [s.name || String(s.id)];
    if (s.role_label) parts.push(s.role_label);
    if (s.pending_count && s.pending_count > 0) parts.push(`${s.pending_count} đơn treo`);
    return parts.join(" - ");
  };

  const filteredStaff = staff.filter((s) => {
    if (filterRole === 'driver') return Number(s.is_driver) === 1;
    if (filterRole === 'counter') return Number(s.is_counter) === 1;
    return true;
  });
  const options = filteredStaff.map((s) => ({ value: s.id, label: buildLabel(s), raw: s }));
  // Người đã gán trước đó nhưng không còn khớp filterRole (VD quyền đã đổi) — vẫn hiển thị để không "mất" NV.
  const extraSelected = value
    .filter((v) => !options.some((o) => o.value === v.id))
    .map((v) => ({ value: v.id, label: v.name || String(v.id), raw: v as PickupStaff }));
  const allOptions = [...options, ...extraSelected];
  const selectedValues = allOptions.filter((o) => value.some((v) => v.id === o.value));

  const emptyLabel =
    filterRole === 'driver' ? 'Không có tài xế' : filterRole === 'counter' ? 'Không có NV kiểm đếm' : 'Không có nhân sự';

  return (
    <Select
      isMulti
      isDisabled={disabled || !branchId}
      placeholder={placeholder || "Chọn nhân viên..."}
      options={allOptions}
      value={selectedValues}
      onChange={(sel: any) =>
        onChange(
          (sel || []).map((o: any) => ({
            id: o.raw.id,
            name: o.raw.name,
            phone: o.raw.phone,
            is_driver: o.raw.is_driver,
            is_counter: o.raw.is_counter,
            role_label: o.raw.role_label,
          })),
        )
      }
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      noOptionsMessage={() => (branchId ? emptyLabel : "Chọn chi nhánh trước")}
    />
  );
};

export default PickupStaffSelect;
